import React, { useState, useRef, useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { FiMinus } from "react-icons/fi";
import dropDownUpArrow from "../../assets/icons/dropDownUpArrow.png";
import dropDownDownArrow from "../../assets/icons/dropDownDownArrow.png";
import { formStyles } from "./FormUtils";
import { trimTextWithEllipsis } from "../../utils/formatting";
import { createPortal } from "react-dom";

const Dropdown = ({
  options = [],
  placeholder = "Select an option",
  defaultOption = null,
  displayKey = "name", // Key for displaying the option
  idKey = "_id", // Key for the unique option identifier
  isSearchable = false,
  searchPlaceholder = "Search...",
  disabled = false,
  wrapperClassName = "",
  buttonClassName = "",
  dropdownClassName = "",
  optionClassName = "",
  filterClassName = "",
  outerContainerStyle = "",
  selectedValueClassName = "",
  selectedButtonClassName = "",
  placeholderClassName = "",
  truncateDisplay = false,
  maxDisplayLength = 20,
  onSelect,
  error,
  label,
  labelStyle,
  isRequired = false,
  name,
  onBlur,
  onFocus,
  allowFillColor = false,
  allowClear = false,
  dropUp = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState(defaultOption);
  const [hasBlurred, setHasBlurred] = useState(false);
  const dropdownRef = useRef(null);
  const optionRefs = useRef([]);
  const [focusedIndex, setFocusedIndex] = useState(0);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    const key = e.key;

    if (!isOpen && (key === "Enter" || key === " ")) {
      e.preventDefault();
      setIsOpen(true);
      setFocusedIndex(0);
      return;
    }

    if (!isOpen) return;

    switch (key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          handleSelect(filteredOptions[focusedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (focusedIndex >= 0 && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [focusedIndex]);

  const handleSelect = (option) => {
    setSelectedOption(option);
    setSearchTerm("");
    setIsOpen(false);
    onSelect && onSelect(option);

    // Trigger form validation
    if (name && onBlur) {
      onBlur(name, option ? getDisplayValue(option) : "");
    }
  };

  const handleBlur = () => {
    if (name && onBlur) {
      onBlur(name, selectedOption ? getDisplayValue(selectedOption) : "");
    }
  };

  const handleButtonClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      onFocus && onFocus();
      setHasBlurred(true);
    }
  };

  useEffect(() => {
    setSelectedOption(defaultOption);
  }, [defaultOption]);

  const getDisplayValue = (option) => {
    if (!option) return "";
    if (displayKey?.includes("+")) {
      // Handle key combination (e.g., "firstName + lastName")
      const keys = displayKey.split("+").map((key) => key.trim());
      return keys.map((key) => option[key]).join(" ");
    }
    return option[displayKey] || "";
  };

  const filteredOptions = options.filter((option) =>
    getDisplayValue(option).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleRemoveSelected = () => {
    setSelectedOption(null);
    onSelect && onSelect(null);

    // Trigger validation when clearing selection
    if (name && onBlur) {
      onBlur(name, "");
    }
  };

  const [dropdownStyle, setDropdownStyle] = useState(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      const frame = requestAnimationFrame(() => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          setDropdownStyle({
            top: dropUp
              ? rect.top + window.scrollY - 265
              : rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      });

      return () => cancelAnimationFrame(frame);
    }
  }, [isOpen]);

  return (
    <div className={twMerge("flex flex-col gap-2 w-full", outerContainerStyle)}>
      {label && (
        <label
          className={twMerge(
            "'text-light-gray font-inter font-medium text-sm",
            labelStyle
          )}
        >
          {label} {isRequired && <span className="text-light-red">*</span>}
        </label>
      )}
      <div ref={dropdownRef} className={`relative ${wrapperClassName}`}>
        <button
          ref={buttonRef}
          onClick={handleButtonClick}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Only trigger blur if dropdown is closed
            if (!isOpen && hasBlurred) {
              handleBlur();
            }
          }}
          className={twMerge(
            `w-full bg-[#2C2C2C] flex text-black items-center border justify-between px-4 py-3 ${
              disabled
                ? "cursor-not-allowed "
                : "bg-[#45444433] border-[#808080] border-[1px] cursor-pointer"
            } ${
              error
                ? "border-light-red"
                : "border-medium-gray focus:border-stoke"
            } ${buttonClassName} ${
              allowFillColor && selectedOption
                ? "bg-[#E0F2F1] bg-opacity-[30%]"
                : ""
            }
    ${isOpen ? " rounded-t-lg" : " rounded-lg"}`,
            selectedButtonClassName
          )}
        >
          <span
            className={twMerge(
              `font-inter text-[#E2E2E2] text-[15px] truncate ${
                selectedOption
                  ? "opacity-100 font-medium"
                  : placeholderClassName ||
                    "text-[#808080] text-[14px] font-normal opacity-50"
              }`,
              selectedValueClassName
            )}
            title={
              typeof selectedOption === "object" && selectedOption !== null
                ? getDisplayValue(selectedOption)
                : selectedOption || placeholder || "Select an option"
            } // Tooltip on hover
          >
            {typeof selectedOption === "object" && selectedOption !== null
              ? truncateDisplay
                ? trimTextWithEllipsis(
                    getDisplayValue(selectedOption),
                    maxDisplayLength
                  )
                : getDisplayValue(selectedOption)
              : truncateDisplay
              ? trimTextWithEllipsis(
                  selectedOption || placeholder || "Select an option",
                  maxDisplayLength
                )
              : selectedOption || placeholder || "Select an option"}
          </span>
          <div className="flex items-center gap-2">
            {selectedOption && !disabled && allowClear !== false && (
              <div
                onClick={(event) => {
                  event.stopPropagation(); // Prevent click event from propagating to the parent button
                  handleRemoveSelected();
                }}
                className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center hover:bg-red-200 cursor-pointer"
              >
                <FiMinus className="w-3 h-3 text-red-500" />
              </div>
            )}
            <img
              src={isOpen ? dropDownDownArrow : dropDownUpArrow}
              alt="arrow"
              className="w-[12px] h-[8px]"
            />
          </div>
        </button>

        {isOpen &&
          dropdownStyle &&
          createPortal(
            <div
              className={`fixed rounded-b-lg bg-[#111111D6] border-[#505050] border z-[9999] ${dropdownClassName}`}
              style={{
                top: dropdownStyle.top,
                left: dropdownStyle.left,
                width: dropdownStyle.width,
                position: "absolute",
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {isSearchable && (
                <div className="p-1 border-b text-black bg-[#454444]">
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    className="w-full px-2 py-2 border-none text-[#808080] rounded bg-[#454444] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              )}

              <div
                className={twMerge`max-h-[200px] overflow-y-auto scrollbar-no-radius ${filterClassName}`}
              >
                {filteredOptions.map((option, index) => (
                  <div
                    key={option[idKey]}
                    ref={(el) => (optionRefs.current[index] = el)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(option);
                    }}
                    className={twMerge(
                      `px-4 py-3 border border-[#808080] pb-1 cursor-pointer`,
                      optionClassName,
                      index === focusedIndex
                        ? "bg-[#565656] text-white font-semibold"
                        : "hover:bg-[#080B12]"
                    )}
                    title={getDisplayValue(option)}
                  >
                    <span className="block truncate text-[#E3E3E3]">
                      {getDisplayValue(option)}
                    </span>
                  </div>
                ))}
                {filteredOptions.length === 0 && (
                  <div className="px-4 py-2 text-gray-500 font-inter font-medium text-center">
                    No results found
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}

        {error && (
          <span className={twMerge(formStyles.errorText)}>{error}</span>
        )}
      </div>
    </div>
  );
};

export default Dropdown;
